import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import { closeModal, Modal, openModal, Select } from "risa-oasis-ui_v2";
import { FacilityProvider } from "../../../../../redux/slice/providerSlice";
import { FirestoreService } from "../../../../../../shared/api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../../../shared/api/firebase/references";
import {
  OrderAssignmentConfigService,
  OrderAssignmentConfiguration,
} from "../../../../../../shared/api/services/orderAssignmentConfigService";
import { generateFullName } from "../../../../../utils/generateOrderDataForTable";
import { HUMAN_MEDICAL_ORDER_STATUSES } from "../utils/medicalOrderStatusConstants";

const ADD_PROVIDER_MODAL_ID = "add-provider-modal";

interface AddProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  facilityId: string;
  onProviderAdded: () => void;
}

const AddProviderModal = ({
  isOpen,
  onClose,
  facilityId,
  onProviderAdded,
}: AddProviderModalProps) => {
  const [selectedProviderId, setSelectedProviderId] = useState<string>("");
  const [providers, setProviders] = useState<FacilityProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Open or close the modal when isOpen changes
  useEffect(() => {
    if (isOpen) {
      openModal(ADD_PROVIDER_MODAL_ID);
    } else {
      closeModal(ADD_PROVIDER_MODAL_ID);
    }
  }, [isOpen]);

  // Fetch providers from healthcare_facility/{orgid}/providers
  useEffect(() => {
    if (!isOpen || !facilityId) {
      setProviders([]);
      return;
    }

    const fetchProviders = async () => {
      setLoading(true);
      try {
        const providersData =
          await FirestoreService.getAllDocuments<FacilityProvider>(
            FirestoreCollectionReference.healthFacilityProviders(facilityId),
          );
        setProviders(providersData);
      } catch (error) {
        console.error("Error fetching providers:", error);
        toast.error("Failed to fetch providers");
        setProviders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, [isOpen, facilityId]);

  // Reset selected provider when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedProviderId("");
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!selectedProviderId) {
      toast.error("Please select a provider");
      return;
    }

    const selectedProvider = providers.find((p) => p.id === selectedProviderId);
    if (!selectedProvider) {
      toast.error("Selected provider not found");
      return;
    }

    setSaving(true);
    try {
      // Check if provider already exists for this facility
      const existingConfigurations =
        await OrderAssignmentConfigService.getConfigurationsByFacilityId(
          facilityId,
        );
      const providerExists = existingConfigurations.some(
        (config) => config.provider_id === selectedProviderId,
      );

      if (providerExists) {
        toast.error("This provider already exists for this facility");
        setSaving(false);
        return;
      }

      const providerName = generateFullName(
        selectedProvider.FirstName || selectedProvider.name || "",
        "",
        selectedProvider.LastName || "",
      );

      const newConfig: Omit<OrderAssignmentConfiguration, "id"> = {
        facility_id: facilityId,
        provider_id: selectedProviderId,
        provider_name: providerName,
        capacity: 50,
        if_multi_regimen: false,
        working_today: false,
        medical_order_status: HUMAN_MEDICAL_ORDER_STATUSES,
        auth_status: ["any"],
        payer_patterns: ["any"],
      };

      await OrderAssignmentConfigService.createConfiguration(newConfig);
      toast.success("Provider added successfully");
      onProviderAdded();
      onClose();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to add provider";
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedProviderId("");
    onClose();
  };

  const providerOptions = providers
    .map((provider) => ({
      label: generateFullName(
        provider.FirstName || provider.name || "",
        "",
        provider.LastName || "",
      ),
      value: provider.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Modal
      dialogId={ADD_PROVIDER_MODAL_ID}
      onSave={handleSave}
      title="Add Provider"
      saveButtonText={saving ? "Adding..." : "Add Provider"}
      cancelText="Cancel"
      onClose={handleCancel}
      onCancel={handleCancel}
      disableSave={!selectedProviderId || saving || loading}
    >
      <div className="flex flex-col gap-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <span className="text-sm leading-[1.375rem] text-[#6B7280]">
              Loading providers...
            </span>
          </div>
        ) : providerOptions.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <span className="text-sm leading-[1.375rem] text-[#6B7280]">
              No providers available for this facility.
            </span>
          </div>
        ) : (
          <div>
            <Select
              id="provider-select"
              label="Select Provider"
              options={providerOptions}
              onOptionChange={(option: { value: string }) => {
                setSelectedProviderId(option.value);
              }}
              placeholder="Select a provider..."
              defaultValue={selectedProviderId || ""}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AddProviderModal;
